import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { fetchPublicTutor } from '@/lib/tutor-api';
import {
  Clock,
  Video,
  BookOpen,
  CalendarCheck,
  CheckCircle2,
  GraduationCap,
  Plus,
  Hourglass,
  ShieldAlert,
  CalendarDays,
  Heart,
} from 'lucide-react';
import {
  fetchMyParentProfile,
  addChild,
  updateChild,
  removeFromShortlist,
} from '@/lib/parent-api';
import { getMyBookings } from '@/lib/booking-api';
import type { PublicTutorView } from '@/lib/schemas/tutor-profile';
import type { ChildResponse, ParentProfileResponse, GradeLevel } from '@/lib/schemas/parent-profile';
import type { BookingResponse } from '@/lib/schemas/booking';
import { GRADE_LEVELS } from '@/lib/schemas/parent-profile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { BookClassModal } from '@/components/BookClassModal';
import { BookingSuccessModal } from '@/components/BookingSuccessModal';
import { StatCard, isUpcoming, formatClassDate, formatTime } from './shared';

// ─── Constants & Types ─────────────────────────────────────────────────────────

type ChildDialogState = null | 'add' | { mode: 'edit'; child: ChildResponse };

interface ChildForm {
  name: string;
  age: string;
  grade: string;
  notes: string;
}

const EMPTY_FORM: ChildForm = {
  name: '',
  age: '',
  grade: 'Grade 3',
  notes: '',
};

const BOOKED_STATUSES = new Set(['pending', 'accepted', 'completed']);
const NEXT_CLASSES_LIMIT = 3;

interface ChildStats {
  upcoming: number;
  completed: number;
  nextClass: BookingResponse | null;
}

const CHILD_AVATAR_COLORS = [
  'from-blue-500 to-indigo-600',
  'from-violet-500 to-purple-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
];

function childAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return CHILD_AVATAR_COLORS[Math.abs(hash) % CHILD_AVATAR_COLORS.length] ?? CHILD_AVATAR_COLORS[0]!;
}

// ─── Sub-Components ───────────────────────────────────────────────────────────

interface NextClassCardProps {
  booking: BookingResponse;
  tutor: PublicTutorView | undefined;
  childName: string | undefined;
}

function NextClassCard({ booking, tutor, childName }: NextClassCardProps) {
  const startTime = new Date(booking.slotStartUtc);
  const endTime = new Date(booking.slotEndUtc);
  const isConfirmed = booking.status === 'accepted';
  const isToday = startTime.toDateString() === new Date().toDateString();

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-700/80 dark:bg-slate-900/30">
      <div
        className={`absolute inset-y-0 left-0 w-1.5 ${
          isToday ? 'bg-brand-600 dark:bg-brand-500' : 'bg-slate-300 dark:bg-slate-700'
        }`}
      />

      <div className="pl-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3.5 min-w-0 flex-1">
          {/* Date Block */}
          <div
            className={`flex h-12 w-12 flex-shrink-0 flex-col items-center justify-center rounded-lg border ${
              isToday
                ? 'bg-brand-50 border-brand-200 text-brand-700 dark:bg-brand-950/35 dark:border-brand-900/30 dark:text-brand-400 font-bold'
                : 'bg-white text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700'
            }`}
          >
            <span className="text-[9px] font-bold uppercase tracking-wider opacity-85">
              {startTime.toLocaleDateString(undefined, { month: 'short' })}
            </span>
            <span className="text-lg font-black leading-none tracking-tight mt-0.5">{startTime.getDate()}</span>
          </div>

          {/* Class info */}
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <h3 className="text-sm font-extrabold text-slate-800 dark:text-slate-100 truncate">
                {booking.classTitle || booking.subject || `Class with ${tutor?.displayName ?? 'tutor'}`}
              </h3>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold border shrink-0 ${
                  isConfirmed
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/40'
                    : 'bg-amber-50 text-amber-700 border-amber-200/50 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/40'
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${isConfirmed ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                {isConfirmed ? 'Confirmed' : 'Awaiting Confirmation'}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-slate-400" />
                {formatTime(startTime)} – {formatTime(endTime)}
              </span>

              {tutor && (
                <span className="flex items-center gap-1 text-slate-700 dark:text-slate-300">
                  {tutor.photoUrl ? (
                    <img
                      src={tutor.photoUrl}
                      className="h-4 w-4 rounded-full object-cover border border-slate-200 dark:border-slate-700"
                      alt=""
                    />
                  ) : (
                    <div className="h-4 w-4 rounded-full bg-blue-100 flex items-center justify-center text-[9px] text-blue-600 font-bold dark:bg-blue-900 dark:text-blue-300">
                      {tutor.displayName.charAt(0)}
                    </div>
                  )}
                  {tutor.displayName}
                </span>
              )}

              {childName && (
                <span className="inline-flex items-center rounded-md bg-blue-50/80 px-2 py-0.5 text-[9px] font-bold text-blue-700 border border-blue-100/60 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/40 uppercase tracking-wider">
                  {childName}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Button */}
        {isConfirmed && booking.videoLink && (
          <div className="shrink-0 w-full sm:w-auto">
            <a
              href={booking.videoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 px-3.5 py-2 text-xs font-bold text-white shadow-sm transition-all active:scale-95 duration-200 cursor-pointer"
            >
              <Video className="h-3.5 w-3.5" />
              <span>Join Class</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

interface ChildCardProps {
  child: ChildResponse;
  stats: ChildStats;
  onEdit: () => void;
  onBookClass: () => void;
}

function ChildCard({ child, stats, onEdit, onBookClass }: ChildCardProps) {
  const nextStart = stats.nextClass ? new Date(stats.nextClass.slotStartUtc) : null;
  const avatarGrad = childAvatarColor(child.name);

  return (
    <div className="group relative h-full flex flex-col justify-between rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5 sm:p-4 transition-all duration-200 hover:border-slate-300 dark:border-slate-700/80 dark:bg-slate-900/30 dark:hover:border-slate-600 shadow-2xs space-y-2.5">
      {/* Top Right Edit Button */}
      <button
        type="button"
        onClick={onEdit}
        className="absolute top-3 right-3 rounded-lg bg-white dark:bg-slate-800 px-2.5 py-1 text-xs font-bold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700 shadow-2xs cursor-pointer active:scale-95 z-10"
      >
        Edit
      </button>

      {/* Header: Avatar + Name & Age/Grade info */}
      <div className="flex flex-wrap items-center gap-3 pr-14">
        {/* Avatar */}
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br shadow-xs ring-2 ring-white dark:ring-slate-800 ${avatarGrad}`}
        >
          <span className="text-base font-black text-white select-none">
            {child.name.charAt(0).toUpperCase()}
          </span>
        </div>

        {/* Name and Age/Grade */}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm sm:text-base font-extrabold text-slate-800 dark:text-slate-100 break-words leading-tight">
            {child.name}
          </h3>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mt-0.5 break-words">
            {child.age} yrs · {child.grade}
          </p>
        </div>
      </div>

      <div className="h-px bg-slate-200/60 dark:bg-slate-800 w-full" />

      {/* Stats Badges */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs font-bold">
        <span className="inline-flex items-center gap-1.25 rounded-md bg-blue-50/80 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/30 px-2 py-0.5 text-blue-700 dark:text-blue-300 text-[11px] font-bold">
          <CalendarCheck className="h-3.5 w-3.5 text-blue-500 shrink-0" />
          <span>{stats.upcoming} Upcoming</span>
        </span>
        <span className="inline-flex items-center gap-1.25 rounded-md bg-emerald-50/80 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900/30 px-2 py-0.5 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
          <span>{stats.completed} Done</span>
        </span>
      </div>

      {/* Next Class Booking status */}
      <div className="text-xs font-semibold w-full">
        {nextStart ? (
          <div className="flex items-start gap-1.5 text-slate-600 dark:text-slate-300 rounded-lg bg-white/70 dark:bg-slate-800/40 p-2 border border-slate-200/50 dark:border-slate-700/50 w-full">
            <Clock className="h-3.5 w-3.5 text-blue-500 shrink-0 mt-0.5" />
            <span className="min-w-0 break-words leading-tight text-xs flex-1">
              Next: <span className="font-bold text-blue-600 dark:text-blue-400">{formatClassDate(nextStart)}</span> at {formatTime(nextStart)}
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/70 dark:bg-slate-800/40 px-2.5 py-1.5 border border-slate-200/50 dark:border-slate-700/50 text-slate-500 dark:text-slate-400 font-medium w-full">
            <span className="flex items-center gap-1.5 min-w-0 text-xs">
              <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="truncate">No upcoming class</span>
            </span>
            <button
              type="button"
              onClick={onBookClass}
              className="font-bold text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors whitespace-nowrap shrink-0 cursor-pointer hover:underline"
            >
              Book a class →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface SavedTutorCardProps {
  tutor: PublicTutorView;
  onRemove: () => void;
  onBook: () => void;
}

function SavedTutorCard({ tutor, onRemove, onBook }: SavedTutorCardProps) {
  return (
    <div className="group relative flex flex-col justify-between rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5 sm:p-4 transition-all duration-200 dark:border-slate-700/80 dark:bg-slate-900/30">
      {/* Heart Favourites Toggle Button (Top Right) */}
      <button
        onClick={onRemove}
        className="absolute top-3 right-3 rounded-full p-1.5 transition-all text-rose-500 hover:text-rose-600 hover:scale-110 active:scale-95 cursor-pointer bg-white dark:bg-slate-800 shadow-xs border border-slate-200 dark:border-slate-700 z-10"
        aria-label="Remove from Favourites"
      >
        <Heart className="h-3.5 w-3.5 fill-rose-500 stroke-rose-500" />
      </button>

      <div>
        <div className="flex items-start gap-3">
          {/* Profile Picture */}
          <div className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xs">
            {tutor.photoUrl ? (
              <img
                src={tutor.photoUrl}
                alt={tutor.displayName}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-500 to-violet-500 text-white font-bold text-sm">
                {tutor.displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="min-w-0 flex-1 space-y-1 pr-7">
            <h3 className="font-extrabold text-slate-800 dark:text-slate-100 truncate text-sm leading-tight">
              {tutor.displayName}
            </h3>

            <div className="flex items-center flex-wrap gap-1.5 text-xs font-semibold">
              {tutor.averageRating !== null && tutor.averageRating !== undefined && (
                <span className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 text-[10px] font-extrabold border border-amber-200/50 dark:border-amber-900/30">
                  ★ {tutor.averageRating.toFixed(1)}
                </span>
              )}
              <span className="text-slate-500 dark:text-slate-400 text-[11px]">
                ${Math.round(tutor.hourlyRate.usdMinor / 100)}/hr
              </span>
            </div>

            {/* Subject tags */}
            <div className="flex flex-wrap gap-1 pt-0.5">
              {tutor.subjects.slice(0, 3).map((subject) => (
                <span
                  key={subject}
                  className="inline-flex rounded bg-blue-50 border border-blue-100 px-1.5 py-0.5 text-[9px] font-extrabold text-blue-600 dark:bg-blue-950/40 dark:border-blue-900/30 dark:text-blue-300"
                >
                  {subject}
                </span>
              ))}
              {tutor.subjects.length > 3 && (
                <span className="inline-flex rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[9px] font-extrabold text-slate-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
                  +{tutor.subjects.length - 3}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 mt-3 pt-2.5 border-t border-slate-200/60 dark:border-slate-800">
        <Link
          to={`/tutors/${tutor.id}`}
          className="flex-1 inline-flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 px-2 py-1.5 text-[11px] font-bold text-slate-700 dark:text-slate-300 transition-colors cursor-pointer"
        >
          View Profile
        </Link>
        <button
          onClick={onBook}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-brand-600 hover:bg-brand-700 px-2 py-1.5 text-[11px] font-bold text-white shadow-xs transition-all active:scale-95 duration-200 cursor-pointer"
        >
          <Plus className="h-3 w-3 flex-shrink-0" />
          Book Class
        </button>
      </div>
    </div>
  );
}

function ParentDashboardSkeleton() {
  return (
    <div className="w-full space-y-6 py-2">
      {/* Stats Overview Skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-full rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 flex items-center justify-between gap-3">
            <div className="space-y-2 flex-1 min-w-0">
              <Skeleton className="h-3.5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full" />
              <Skeleton className="h-8 w-10 bg-slate-200 dark:bg-slate-700 rounded-lg" />
            </div>
            <Skeleton className="h-10 w-10 bg-slate-200 dark:bg-slate-700 rounded-lg shrink-0" />
          </div>
        ))}
      </div>

      {/* Main Grid Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Next Classes Card Skeleton */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 pt-3.5 sm:px-5 sm:pb-5 sm:pt-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 space-y-3">
            <div className="flex justify-between items-center mb-3">
              <Skeleton className="h-4 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
              <Skeleton className="h-4 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
            <div className="space-y-3">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="rounded-xl border border-slate-100 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-900/30 p-4 flex items-center gap-4">
                  <Skeleton className="h-12 w-12 bg-slate-200 dark:bg-slate-700 rounded-lg shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3 bg-slate-200 dark:bg-slate-700 rounded" />
                    <Skeleton className="h-3.5 w-1/2 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Children Card Skeleton */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 pt-3.5 sm:px-5 sm:pb-5 sm:pt-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 space-y-3">
            <div className="flex justify-between items-center mb-3">
              <Skeleton className="h-4 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
              <Skeleton className="h-8 w-24 bg-slate-200 dark:bg-slate-700 rounded-lg" />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="rounded-xl border border-slate-100 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-900/30 p-4 flex items-start gap-3.5">
                  <Skeleton className="h-12 w-12 bg-slate-200 dark:bg-slate-700 rounded-xl shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                    <Skeleton className="h-3.5 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-1 flex flex-col justify-between h-full space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-4 pt-3.5 sm:px-5 sm:pb-5 sm:pt-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 flex-1 flex flex-col justify-between space-y-3">
            <div className="flex justify-between items-center mb-3">
              <Skeleton className="h-4 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
              <Skeleton className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
            <div className="space-y-3">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="rounded-xl border border-slate-100 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-900/30 p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-12 w-12 bg-slate-200 dark:bg-slate-700 rounded-xl shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                      <Skeleton className="h-3.5 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ParentDashboard() {
  const { accessToken } = useAuth();
  const [profile, setProfile] = useState<ParentProfileResponse | null>(null);
  const [bookings, setBookings] = useState<BookingResponse[]>([]);
  const [tutorData, setTutorData] = useState<Record<string, PublicTutorView>>({});
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<ChildDialogState>(null);
  const [form, setForm] = useState<ChildForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [activeBookingTutor, setActiveBookingTutor] = useState<PublicTutorView | null>(null);
  const [activeBookingChildId, setActiveBookingChildId] = useState<string | null>(null);
  const [isBookModalOpen, setIsBookModalOpen] = useState(false);

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successDetails, setSuccessDetails] = useState<{
    summaryMessage: string;
    tutorName: string;
    childName: string;
  } | null>(null);

  const openBookClassForChild = (childId: string) => {
    setActiveBookingChildId(childId);
    setActiveBookingTutor(null);
    setIsBookModalOpen(true);
  };

  const refreshBookings = useCallback(async () => {
    if (!accessToken) return;
    try {
      const b = await getMyBookings(accessToken);
      setBookings(b);
    } catch (e) {
      console.error('[ParentHome] Failed to refresh bookings:', e);
    }
  }, [accessToken]);

  const handleBookingComplete = useCallback(() => {
    refreshBookings();
  }, [refreshBookings]);

  const handleBookingSuccess = useCallback((details: {
    summaryMessage: string;
    tutorName: string;
    childName: string;
  }) => {
    setSuccessDetails(details);
    setSuccessModalOpen(true);
  }, []);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const p = await fetchMyParentProfile(accessToken);
        if (cancelled) return;
        setProfile(p);

        let b: BookingResponse[] = [];
        try {
          b = await getMyBookings(accessToken);
        } catch {
          // Bookings API failed silently
        }

        if (cancelled) return;
        setBookings(b);

        const now = Date.now();
        const upcoming = b.filter((booking) => isUpcoming(booking, now));
        const tutorIds = new Set([
          ...upcoming.map((booking) => booking.tutorId),
          ...p.shortlistedTutorIds,
        ]);

        const tutors: Record<string, PublicTutorView> = {};
        for (const tutorId of tutorIds) {
          try {
            const tutor = await fetchPublicTutor(accessToken, tutorId);
            if (!cancelled) {
              tutors[tutorId] = tutor;
            }
          } catch {
            // Tutor data is optional
          }
        }

        if (!cancelled) {
          setTutorData(tutors);
          setLoading(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const message = e instanceof ApiError ? e.message : String(e);
          console.error('[HomePage] Profile load error:', message, e);
          setPageError(e instanceof ApiError ? e.message : 'Failed to load profile');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const openAddDialog = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialog('add');
  };

  const openEditDialog = (child: ChildResponse) => {
    setForm({
      name: child.name,
      age: String(child.age),
      grade: child.grade,
      notes: child.notes ?? '',
    });
    setFormError(null);
    setDialog({ mode: 'edit', child });
  };

  const closeDialog = useCallback(() => setDialog(null), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken || dialog === null) return;
    const age = parseInt(form.age, 10);
    if (isNaN(age) || age < 1 || age > 25) {
      setFormError('Age must be between 1 and 25');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const grade = form.grade as GradeLevel;
      const body = { name: form.name.trim(), age, grade, notes: form.notes.trim() || null };
      const updated =
        dialog === 'add'
          ? await addChild(accessToken, body)
          : await updateChild(accessToken, dialog.child.id, body);
      setProfile(updated);
      setDialog(null);
    } catch (err: unknown) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveShortlist = async (tutorId: string) => {
    if (!accessToken) return;
    try {
      await removeFromShortlist(accessToken, tutorId);
      setProfile((p) =>
        p
          ? {
              ...p,
              shortlistedTutorIds: p.shortlistedTutorIds.filter((id) => id !== tutorId),
            }
          : p,
      );
    } catch {
      setPageError('Failed to remove from shortlist — please try again');
    }
  };

  const isEditDialog = dialog !== null && dialog !== 'add';

  // Derived dashboard data
  const { upcomingBookings, totalBooked, completedCount, pendingCount, childStatsById, childNameById } =
    useMemo(() => {
      const now = Date.now();
      const upcoming = bookings
        .filter((b) => isUpcoming(b, now))
        .sort((a, b) => new Date(a.slotStartUtc).getTime() - new Date(b.slotStartUtc).getTime());

      const statsById: Record<string, ChildStats> = {};
      for (const child of profile?.children ?? []) {
        const childUpcoming = upcoming.filter((b) => b.childId === child.id);
        statsById[child.id] = {
          upcoming: childUpcoming.length,
          completed: bookings.filter(
            (b) => b.childId === child.id && b.status === 'completed',
          ).length,
          nextClass: childUpcoming[0] ?? null,
        };
      }

      const nameById: Record<string, string> = {};
      for (const child of profile?.children ?? []) nameById[child.id] = child.name;

      return {
        upcomingBookings: upcoming,
        totalBooked: bookings.filter((b) => BOOKED_STATUSES.has(b.status)).length,
        completedCount: bookings.filter((b) => b.status === 'completed').length,
        pendingCount: upcoming.filter((b) => b.status === 'pending').length,
        childStatsById: statsById,
        childNameById: nameById,
      };
    }, [bookings, profile]);

  if (loading) {
    return <ParentDashboardSkeleton />;
  }

  const nextClasses = upcomingBookings.slice(0, NEXT_CLASSES_LIMIT);
  const children = profile?.children ?? [];

  return (
    <div className="w-full space-y-6 py-2 animate-fade-in">
      {pageError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50/70 p-4 text-sm font-semibold text-red-700 dark:border-red-900/50 dark:bg-red-950/20 backdrop-blur-md flex items-center gap-2"
        >
          <ShieldAlert className="h-5 w-5 shrink-0 text-red-500" />
          <span>{pageError}</span>
        </div>
      )}

      {/* Stats overview */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          <StatCard
            icon={<BookOpen className="h-5 w-5" />}
            label="Total Booked"
            value={totalBooked}
            textAccent="text-slate-900 dark:text-white"
          />
          <StatCard
            icon={<CalendarCheck className="h-5 w-5" />}
            label="Upcoming Sessions"
            value={upcomingBookings.length}
            textAccent="text-slate-900 dark:text-white"
          />
          <StatCard
            icon={<CheckCircle2 className="h-5 w-5" />}
            label="Completed Sessions"
            value={completedCount}
            textAccent="text-slate-900 dark:text-white"
          />
          <StatCard
            icon={<Hourglass className="h-5 w-5" />}
            label="Awaiting Tutor"
            value={pendingCount}
            textAccent="text-slate-900 dark:text-white"
          />
        </div>
      )}

      {/* Grid: Next Classes & Favourite Tutors */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* Left column (Next Classes and Children) */}
        <div className="lg:col-span-2 space-y-6">

          {/* Next classes card */}
          <fieldset className="rounded-lg border border-slate-200 bg-white px-4 pb-4 pt-1 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <legend className="px-2">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Next Classes
              </span>
            </legend>

            {upcomingBookings.length > NEXT_CLASSES_LIMIT && (
              <div className="flex justify-end mb-2">
                <Link
                  to="/bookings"
                  className="text-xs font-bold text-brand-600 hover:text-brand-700 dark:text-brand-400"
                >
                  View all ({upcomingBookings.length}) &rarr;
                </Link>
              </div>
            )}

            {nextClasses.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/25 p-8 text-center space-y-2">
                <CalendarDays className="mx-auto h-8 w-8 text-slate-400 dark:text-slate-500" />
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                  No upcoming classes scheduled.{' '}
                  <Link
                    to="/tutors"
                    className="font-bold text-brand-600 hover:text-brand-700 dark:text-brand-400 hover:underline"
                  >
                    Find a tutor
                  </Link>{' '}
                  to get started.
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {nextClasses.map((booking) => (
                  <li key={booking.id}>
                    <NextClassCard
                      booking={booking}
                      tutor={tutorData[booking.tutorId]}
                      childName={childNameById[booking.childId]}
                    />
                  </li>
                ))}
              </ul>
            )}
          </fieldset>

          {/* Children Section card */}
          <fieldset className="rounded-lg border border-slate-200 bg-white px-4 pb-4 pt-1 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <legend className="px-2">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Your Children
              </span>
            </legend>

            <div className="flex justify-end mb-2">
              <Button size="sm" onClick={openAddDialog} className="rounded-md font-semibold text-xs h-7 px-2.5 bg-brand-600 hover:bg-brand-700 text-white shadow-sm cursor-pointer">
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Child
              </Button>
            </div>

            {children.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/25 p-8 text-center space-y-2">
                <GraduationCap className="mx-auto h-8 w-8 text-slate-400 dark:text-slate-500" />
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                  No children profiles added yet. Add one to enroll them in classes.
                </p>
              </div>
            ) : (
              <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {children.map((child) => (
                  <li key={child.id}>
                    <ChildCard
                      child={child}
                      stats={
                        childStatsById[child.id] ?? { upcoming: 0, completed: 0, nextClass: null }
                      }
                      onEdit={() => openEditDialog(child)}
                      onBookClass={() => openBookClassForChild(child.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </fieldset>
        </div>

        {/* Right column (Favourite Tutors) */}
        <div className="lg:col-span-1 flex flex-col h-full">
          <fieldset className="rounded-lg border border-slate-200 bg-white px-4 pb-4 pt-1 shadow-sm dark:border-slate-700 dark:bg-slate-800 flex-1 flex flex-col">
            <legend className="px-2">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Favourite Tutors
              </span>
            </legend>

            <div className="flex justify-end mb-2">
              <Link
                to="/tutors"
                className="text-xs font-bold text-brand-600 hover:text-brand-700 dark:text-brand-400"
              >
                Find Tutors &rarr;
              </Link>
            </div>

            {!loading && (profile === null || profile.shortlistedTutorIds.length === 0) && (
              <div className="rounded-lg border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-800/25 p-6 text-center space-y-2">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  No tutors saved in your favourites yet.{' '}
                  <Link
                    to="/tutors"
                    className="font-bold text-brand-600 hover:text-brand-700 dark:text-brand-400 hover:underline"
                  >
                    Browse tutors
                  </Link>{' '}
                  to save your favorite educators.
                </p>
              </div>
            )}

            {profile !== null && profile.shortlistedTutorIds.length > 0 && (
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
                {profile.shortlistedTutorIds.map((tutorId) => {
                  const tutor = tutorData[tutorId];
                  return (
                    <li key={tutorId}>
                      {tutor ? (
                        <SavedTutorCard
                          tutor={tutor}
                          onRemove={() => handleRemoveShortlist(tutorId)}
                          onBook={() => setActiveBookingTutor(tutor)}
                        />
                      ) : (
                        <div className="flex items-center justify-center rounded-lg border border-slate-100 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/30">
                          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 animate-pulse">
                            Loading tutor credentials...
                          </p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>
        </div>
      </div>

      {/* Child dialog */}
      <Dialog
        open={dialog !== null}
        onClose={closeDialog}
        title={isEditDialog ? 'Edit child' : 'Add child'}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="child-name" className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Name</Label>
            <Input
              id="child-name"
              required
              maxLength={120}
              placeholder="Enter child's name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700"
            />
          </div>

          <div>
            <Label htmlFor="child-age" className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Age</Label>
            <Input
              id="child-age"
              type="number"
              required
              min={1}
              max={25}
              placeholder="e.g., 9"
              value={form.age}
              onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))}
              className="mt-1.5 rounded-xl border-slate-200 dark:border-slate-700"
            />
          </div>

          <div>
            <Label htmlFor="child-grade" className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Grade</Label>
            <select
              id="child-grade"
              required
              value={form.grade}
              onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-500"
            >
              {GRADE_LEVELS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="child-notes" className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Notes (optional)</Label>
            <textarea
              id="child-notes"
              maxLength={500}
              rows={3}
              placeholder="Add any learning preferences or notes about your child..."
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:ring-blue-500"
            />
          </div>

          {formError && (
            <div
              role="alert"
              className="rounded-xl bg-red-50 px-4 py-2.5 text-xs font-semibold text-red-600 dark:bg-red-950/20 dark:text-red-400"
            >
              {formError}
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-5 dark:border-slate-800">
            <Button type="button" variant="outline" onClick={closeDialog} className="rounded-xl font-bold cursor-pointer">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="rounded-xl font-bold cursor-pointer">
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </Dialog>

      {(activeBookingTutor || isBookModalOpen) && accessToken && (
        <BookClassModal
          open={activeBookingTutor !== null || isBookModalOpen}
          onClose={() => {
            setActiveBookingTutor(null);
            setIsBookModalOpen(false);
            setActiveBookingChildId(null);
          }}
          accessToken={accessToken}
          childOptions={children}
          initialTutor={activeBookingTutor}
          initialChildId={activeBookingChildId}
          onBooked={handleBookingComplete}
          onSuccess={handleBookingSuccess}
        />
      )}

      {successDetails && (
        <BookingSuccessModal
          open={successModalOpen}
          onClose={() => {
            setSuccessModalOpen(false);
            setSuccessDetails(null);
          }}
          summaryMessage={successDetails.summaryMessage}
          tutorName={successDetails.tutorName}
          childName={successDetails.childName}
        />
      )}
    </div>
  );
}
